import { computed, inject, Injectable, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  FileManagerService,
  getDownloadURL$,
} from '@principle-theorem/ng-shared';
import {
  IMohFormDisplay,
  MohFormDisplayLabel,
} from '@principle-theorem/principle-core/interfaces';
import { Observable, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { Storage } from '@angular/fire/storage';
import { toObservable } from '@angular/core/rxjs-interop';
import { getError } from '@principle-theorem/shared';
import { clone } from 'lodash';

@Injectable()
export class MohFormViewerService {
  private _storage = inject(Storage);
  private _sanitiser = inject(DomSanitizer);
  private _fileManager = inject(FileManagerService);

  downloadedForms = signal<
    Partial<Record<MohFormDisplayLabel, boolean>> | undefined
  >(undefined);
  activeForm = signal<IMohFormDisplay | undefined>(undefined);
  uploading = signal(false);

  uploadDisabled = computed(() => {
    const activeForm = this.activeForm();
    const downloadedForms = this.downloadedForms();
    return (
      !downloadedForms || !activeForm || !downloadedForms[activeForm.label]
    );
  });
  safeUrl$: Observable<SafeResourceUrl | undefined>;

  constructor() {
    this.safeUrl$ = this.getSafeUrl$();
  }

  getSafeUrl$(): Observable<SafeResourceUrl | undefined> {
    return toObservable(this.activeForm).pipe(
      switchMap((form) =>
        form
          ? getDownloadURL$(this._storage, form.formPath).pipe(
              map((url) => this._sanitiser.bypassSecurityTrustResourceUrl(url))
            )
          : of(undefined)
      )
    );
  }

  async downloadForm(): Promise<void> {
    const form = this.activeForm();
    if (!form) {
      return;
    }
    await this._fileManager.downloadFile({
      path: form.formPath,
      name: `${form.label}.pdf`,
    });
    this.markFormAsDownloaded(form);
  }

  markFormAsDownloaded(form: IMohFormDisplay): void {
    this.downloadedForms.update((previous) => ({
      ...previous,
      [form.label]: true,
    }));
  }

  async uploadForm(
    event: Event,
    inputElement?: HTMLInputElement
  ): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    const form = this.activeForm();
    if (!file || !form) {
      return;
    }
    try {
      this.uploading.set(true);
      await this._fileManager.uploadFile(form.formPath, file);
      this.activeForm.set(clone(form));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
    } finally {
      if (inputElement) {
        inputElement.value = '';
      }
      this.uploading.set(false);
    }
  }
}
